import * as constants from '../constants/CONSTANT';
import jwt_decode from 'jwt-decode';

/** Shared across tabs: any tab with user activity updates this so idle timeout is global, not per-tab. */
export const LAST_USER_ACTIVITY_SYNC_KEY = 'spark_last_activity_ts';

function touchSyncedLastActivity() {
  try {
    localStorage.setItem(LAST_USER_ACTIVITY_SYNC_KEY, String(Date.now()));
  } catch (e) {
    // ignore quota / private mode
  }
}

const authApi = {
  async login(username, password, tcode) {
    let response = await fetch(constants.API_BASE_URL + '/api/auth/login', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: username,
        password: password,
        tcode: tcode,
      }),
    });

    const result = await response.json();
    if (result.success) {
      localStorage.setItem('token', result.authToken);
      touchSyncedLastActivity();
      try {
        const decoded = jwt_decode(result.authToken);
        if (decoded?.tenantcode) {
          localStorage.setItem('tenantcode', decoded.tenantcode);
        }
      } catch (error) {
        // ignore token decode errors
      }
      if (result.refreshToken) {
        localStorage.setItem('refreshToken', result.refreshToken);
      }
      if (result.authSettings) {
        localStorage.setItem(
          'authSettings',
          JSON.stringify(result.authSettings)
        );
      }
    }
    return result;
  },
  async social_login(google_profile, token) {
    const { email, name, picture, sub } = google_profile;
    ////
    let response = await fetch(
      constants.API_BASE_URL + '/api/auth/social_login',
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          name: name,
          picture: picture.data ? picture.data.url : picture,
          sub: sub,
          password: token,
        }),
      }
    );

    const result = await response.json();

    if (result.success) {
      localStorage.setItem('token', result.authToken);
      touchSyncedLastActivity();
      try {
        const decoded = jwt_decode(result.authToken);
        if (decoded?.tenantcode) {
          localStorage.setItem('tenantcode', decoded.tenantcode);
        }
      } catch (error) {
        // ignore token decode errors
      }
      if (result.refreshToken) {
        localStorage.setItem('refreshToken', result.refreshToken);
      }
      if (result.authSettings) {
        localStorage.setItem(
          'authSettings',
          JSON.stringify(result.authSettings)
        );
      }
    }
    return result;
  },

  async refreshToken() {
    const refreshToken = localStorage.getItem('refreshToken');
    const tenantcode = localStorage.getItem('tenantcode');
    if (!refreshToken || !tenantcode) {
      return false;
    }

    const response = await fetch(
      constants.API_BASE_URL + '/api/auth/refresh',
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken, tenantcode }),
      }
    );

    if (!response.ok) {
      return false;
    }

    const result = await response.json();
    if (result.success && result.authToken) {
      localStorage.setItem('token', result.authToken);
      touchSyncedLastActivity();
      try {
        const decoded = jwt_decode(result.authToken);
        if (decoded?.tenantcode) {
          localStorage.setItem('tenantcode', decoded.tenantcode);
        }
      } catch (error) {
        // ignore token decode errors
      }
      if (result.refreshToken) {
        localStorage.setItem('refreshToken', result.refreshToken);
      }
      if (result.authSettings) {
        localStorage.setItem(
          'authSettings',
          JSON.stringify(result.authSettings)
        );
      }
      return true;
    }

    return false;
  },

  async fetchEntitlements() {
    const token = localStorage.getItem('token');
    if (!token) return { success: false };
    const response = await fetch(constants.API_BASE_URL + '/api/auth/entitlements', {
      method: 'GET',
      headers: { Authorization: token },
    });
    const result = await response.json();
    if (result.success && result.entitlements) {
      console.log('[authApi] fetchEntitlements response enabledPackages:', result.entitlements?.enabledPackages);
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('entitlements', JSON.stringify(result.entitlements));
      }
      return result;
    }
    return { success: false };
  },

  async fetchMyImage() {
    ////
    const token = localStorage.getItem('token');
    let response = await fetch(constants.API_BASE_URL + '/api/auth/myimage', {
      method: 'GET',
      //mode: "cors",

      headers: {
        Authorization: token,
      },
    });
    ////
    if (response.status === 200) {
      const fileBody = await response.blob();
      return fileBody;
    } else {
      return null;
    }
  },

  async fetchUserImage(userid) {
    ////
    const token = localStorage.getItem('token');
    let response = await fetch(
      constants.API_BASE_URL + '/api/auth/userimage/' + userid,
      {
        method: 'GET',
        //mode: "cors",

        headers: {
          Authorization: token,
        },
      }
    );
    ////
    const fileBody = await response.blob();
    return fileBody;
  },

  logout(reason = 'LOGOUT') {
    const isSessionTimeout =
      reason === 'SESSION_EXPIRED' || reason === 'IDLE_TIMEOUT';

    if (isSessionTimeout) {
      const message =
        reason === 'IDLE_TIMEOUT'
          ? 'You have been logged out due to inactivity. Please log in again.'
          : 'You have been logged out. Please log in again.';
      sessionStorage.setItem('logout_reason', reason);
      sessionStorage.setItem('logout_message', message);
    } else {
      sessionStorage.removeItem('logout_reason');
      sessionStorage.removeItem('logout_message');
    }

    // Build login URL: for session timeout only, append redirect as URL param (not session)
    const excludedPaths = [
      '/login',
      '/reset-password',
      '/google-auth-callback',
      '/402',
      '/403',
      '/404',
    ];
    const currentPath = window.location.pathname;
    const isExcludedPath = excludedPaths.some(
      path => currentPath === path || currentPath.startsWith(path)
    );
    const hasValidRedirect =
      isSessionTimeout && !isExcludedPath && currentPath !== '/';
    const redirectValue = hasValidRedirect
      ? currentPath + window.location.search
      : null;

    const refreshToken = localStorage.getItem('refreshToken');
    const tenantcode = localStorage.getItem('tenantcode');
    if (refreshToken && tenantcode) {
      fetch(constants.API_BASE_URL + '/api/auth/logout', {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken, tenantcode }),
      });
    }

    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tenantcode');
    localStorage.removeItem('authSettings');
    localStorage.removeItem(LAST_USER_ACTIVITY_SYNC_KEY);
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('r-t');
    sessionStorage.removeItem('userinfo');
    sessionStorage.removeItem('user_permissions');
    sessionStorage.removeItem('user_permissions_timestamp');
    sessionStorage.removeItem('entitlements');

    const loginUrl = redirectValue
      ? `/login?redirect=${encodeURIComponent(redirectValue)}`
      : '/login';
    window.location.href = loginUrl;
  },

  async bs() {
    ////
    return 'yes';
  },
};

export default authApi;
